import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FadeIn, StaggerContainer, StaggerItem, RevealText, SlideReveal } from '../components/AnimatedSection';
import { FloatingHexagons, HexShape } from '../components/HexagonLogo';
import { MarqueeText } from '../components/MarqueeScroll';
import { ParallaxImage, RevealImage, WordReveal, AnimatedCounter, HoverImage, MagneticButton } from '../components/ScrollEffects';
import { HiArrowRight } from 'react-icons/hi';

export default function Home() {
  return (
    <div className="page-wrapper">
      {/* Hero with background image */}
      <section style={styles.hero}>
        <div style={styles.heroBg}>
          <motion.img
            src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=80"
            alt="Industrial manufacturing"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <div style={styles.heroOverlay} />
        </div>
        <FloatingHexagons />
        <div style={styles.heroLine} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={styles.heroContent}>
            <FadeIn delay={0.2}>
              <div style={styles.heroLabel}>
                <div style={styles.heroDot} />
                Hive &middot; Thrive &middot; Ventive
              </div>
            </FadeIn>

            <RevealText>
              <h1 style={styles.heroTitle}>
                The AI Engine<br />
                Behind Industrial<br />
                India's <span style={styles.heroAccent}>Digital</span><br />
                <span style={styles.heroAccent}>Future.</span>
              </h1>
            </RevealText>

            <FadeIn delay={0.6}>
              <p style={styles.heroSub}>
                We help manufacturers build powerful digital brands<br />
                using AI-powered video, content, research & web design.
              </p>
            </FadeIn>

            <FadeIn delay={0.75}>
              <div className="flex-btns">
                <MagneticButton>
                  <Link to="/contact" className="btn btn-primary">
                    Start Your Journey <HiArrowRight />
                  </Link>
                </MagneticButton>
                <MagneticButton>
                  <Link to="/case-study" className="btn btn-outline">
                    View Case Study
                  </Link>
                </MagneticButton>
              </div>
            </FadeIn>
          </div>
        </div>

        <div style={styles.heroBottom}>
          <HexShape size={16} color="#3E7562" opacity={0.3} />
          <div style={{ flex: 1, height: 1, background: 'rgba(62, 117, 98, 0.1)' }} />
          <HexShape size={16} color="#3E7562" opacity={0.3} />
        </div>
      </section>

      {/* Stats Marquee */}
      <section style={{ background: '#111112', padding: '20px 0', borderTop: '1px solid rgba(62,117,98,0.08)', borderBottom: '1px solid rgba(62,117,98,0.08)' }}>
        <MarqueeText
          texts={['AI Video Production', 'Market Research', 'Content Creation', 'Web Development', 'Digital Strategy']}
          speed={35}
        />
      </section>

      {/* Parallax Image Break */}
      <ParallaxImage
        src="https://images.unsplash.com/photo-1565043666747-69f6646db940?w=1920&q=80"
        alt="Factory production line"
        height={450}
        speed={0.3}
      />

      {/* Problem Section with image */}
      <section className="section" style={{ background: '#111112' }}>
        <div className="container">
          <div className="grid-2" style={{ gap: 80, alignItems: 'center' }}>
            <div>
              <FadeIn>
                <div className="section-num">02 &mdash; THE PROBLEM</div>
              </FadeIn>
              <WordReveal
                text="We know your problems because we've lived them."
                className="section-title"
                style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, marginBottom: 20, display: 'block' }}
              />
              <FadeIn delay={0.3}>
                <p className="section-subtitle">
                  We solved them — for ourselves, and now for you. Your product quality
                  is world-class. Your digital presence isn't.
                </p>
              </FadeIn>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {problemStats.map((stat, i) => (
                <SlideReveal key={i} direction="right" delay={i * 0.12}>
                  <motion.div
                    style={styles.statRow}
                    whileHover={{ x: 8, borderColor: 'rgba(62, 117, 98, 0.3)' }}
                  >
                    <span style={styles.statNum}>{stat.num}</span>
                    <div>
                      <p style={styles.statLabel}>{stat.label}</p>
                      <p style={styles.statDesc}>{stat.desc}</p>
                    </div>
                  </motion.div>
                </SlideReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services with images */}
      <section className="section">
        <div className="container">
          <FloatingHexagons />
          <div style={{ textAlign: 'center', position: 'relative', zIndex: 2, marginBottom: 72 }}>
            <FadeIn>
              <div className="section-num" style={{ justifyContent: 'center' }}>04 &mdash; SERVICES</div>
            </FadeIn>
            <WordReveal
              text="Four services. One mission. Make you impossible to ignore."
              className="section-title"
              style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.15, display: 'block' }}
            />
          </div>

          <StaggerContainer className="grid-2" staggerDelay={0.12}>
            {serviceCards.map((s, i) => (
              <StaggerItem key={i}>
                <motion.div
                  style={styles.serviceCard}
                  whileHover={{ y: -6, borderColor: 'rgba(62, 117, 98, 0.25)' }}
                  transition={{ duration: 0.3 }}
                >
                  <RevealImage
                    src={s.image}
                    alt={s.title}
                    style={{ width: '100%', height: 220, marginBottom: 24 }}
                  />
                  <div style={styles.serviceTop}>
                    <span style={styles.serviceNum}>0{i + 1}</span>
                    <HexShape size={32} color="#3E7562" opacity={0.2} />
                  </div>
                  <h3 style={styles.serviceTitle}>{s.title}</h3>
                  <p style={styles.serviceDesc}>{s.desc}</p>
                  <div style={styles.serviceTags}>
                    {s.tags.map((tag, j) => (
                      <span key={j} style={styles.serviceTag}>{tag}</span>
                    ))}
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <FadeIn style={{ textAlign: 'center', marginTop: 56 }}>
            <MagneticButton>
              <Link to="/services" className="btn btn-outline">
                Explore All Services <HiArrowRight />
              </Link>
            </MagneticButton>
          </FadeIn>
        </div>
      </section>

      {/* Portfolio / Work Showcase */}
      <section className="section" style={{ background: '#111112' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <FadeIn>
              <div className="section-num" style={{ justifyContent: 'center' }}>05 &mdash; OUR WORK</div>
              <h2 className="section-title">
                Recent <span className="gradient-text">projects.</span>
              </h2>
            </FadeIn>
          </div>

          <div className="grid-3">
            {portfolioImages.map((item, i) => (
              <FadeIn key={i} delay={i * 0.15}>
                <HoverImage
                  src={item.src}
                  alt={item.label}
                  overlay={item.label}
                  style={{ width: '100%', height: 320 }}
                />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* AI Stats with animated counters */}
      <section className="section" style={{ background: '#0B0B0B' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 72 }}>
            <FadeIn>
              <div className="section-num" style={{ justifyContent: 'center' }}>06 &mdash; WHY AI</div>
              <h2 className="section-title">
                Your competitors without AI<br />
                are <span className="gradient-text">already behind.</span>
              </h2>
            </FadeIn>
          </div>

          <StaggerContainer className="grid-4" staggerDelay={0.1}>
            {aiStats.map((stat, i) => (
              <StaggerItem key={i}>
                <motion.div
                  style={styles.aiCard}
                  whileHover={{ borderColor: 'rgba(62, 117, 98, 0.3)' }}
                >
                  <AnimatedCounter value={stat.num} suffix="" duration={2} />
                  <h4 style={styles.aiLabel}>{stat.label}</h4>
                  <p style={styles.aiDesc}>{stat.desc}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Parallax break before CTA */}
      <ParallaxImage
        src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920&q=80"
        alt="Team collaboration"
        height={400}
        speed={0.25}
      />

      {/* CTA Section */}
      <section className="section">
        <div className="container">
          <FadeIn>
            <div style={styles.ctaBlock}>
              <FloatingHexagons />
              <div style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <RevealText>
                  <h2 style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', fontWeight: 800, marginBottom: 20, letterSpacing: '-1.5px' }}>
                    The manufacturers who go<br />
                    digital in 2025 will <span className="gradient-text">dominate</span><br />
                    their category for a decade.
                  </h2>
                </RevealText>
                <p style={{ color: '#888', fontSize: '1rem', marginBottom: 36, maxWidth: 500, margin: '0 auto 36px' }}>
                  The question is not whether to start. It's whether you start before your competitor does.
                </p>
                <MagneticButton>
                  <Link to="/contact" className="btn btn-primary" style={{ padding: '18px 48px' }}>
                    Let's Talk <HiArrowRight />
                  </Link>
                </MagneticButton>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

const problemStats = [
  { num: '83%', label: 'of B2B buyers research online before calling', desc: "If you're invisible online, you lose deals before they start." },
  { num: '9/10', label: 'Telangana manufacturers have no digital presence', desc: "Your product quality is world-class. Your digital presence isn't." },
  { num: '5x', label: 'faster — competitors are capturing your buyers', desc: 'Gujarat & Maharashtra manufacturers are winning online. Right now.' },
];

const serviceCards = [
  {
    title: 'AI Video Production',
    desc: 'AI presenters, product demos, factory showcases & social reels — broadcast quality at a fraction of traditional cost.',
    tags: ['AI Presenters', 'Product Films', 'Social Reels'],
    image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&q=80',
  },
  {
    title: 'Market Research',
    desc: 'Competitor mapping, customer segmentation, geography & pain point analysis — evidence-based decisions.',
    tags: ['Competitors', 'Segments', 'Pain Points'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
  },
  {
    title: 'AI Content Creation',
    desc: 'SEO content, social posts, brochures, catalogues — AI-powered, consistently on-brand.',
    tags: ['SEO Content', 'Social Media', 'Brochures'],
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80',
  },
  {
    title: 'Web Design & Dev',
    desc: 'Fast, beautiful, conversion-optimised websites built in days — not months. Ongoing support included.',
    tags: ['Design', 'Development', 'Support'],
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&q=80',
  },
];

const portfolioImages = [
  { src: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=800&q=80', label: 'AI Video — Product Demo' },
  { src: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', label: 'Market Intelligence Report' },
  { src: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&q=80', label: 'Brand Identity — Web Design' },
];

const aiStats = [
  { num: '10x', label: 'Faster Content', desc: 'AI produces video, copy, and creative in hours.' },
  { num: '80%', label: 'Lower Cost', desc: 'AI eliminates crew, studios, and re-shoots.' },
  { num: '24/7', label: 'Always On', desc: 'Content works around the clock.' },
  { num: '5x', label: 'Greater Reach', desc: 'Your brand visible everywhere buyers look.' },
];

const styles = {
  hero: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    paddingTop: 120,
    paddingBottom: 80,
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,11,11,0.85) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0.92) 100%)',
  },
  heroLine: {
    position: 'absolute',
    left: '10%',
    top: 0,
    bottom: 0,
    width: 1,
    background: 'linear-gradient(180deg, transparent 0%, rgba(62,117,98,0.08) 30%, rgba(62,117,98,0.08) 70%, transparent 100%)',
    zIndex: 1,
  },
  heroContent: { maxWidth: 700, position: 'relative', zIndex: 2 },
  heroLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: 4,
    textTransform: 'uppercase',
    color: '#549E84',
    marginBottom: 32,
  },
  heroDot: {
    width: 6,
    height: 6,
    background: '#3E7562',
    boxShadow: '0 0 12px #3E7562',
  },
  heroTitle: {
    fontSize: 'clamp(3rem, 7vw, 5rem)',
    fontWeight: 800,
    lineHeight: 1.02,
    letterSpacing: '-3px',
    marginBottom: 28,
    color: '#fff',
  },
  heroAccent: {
    color: '#549E84',
  },
  heroSub: {
    fontSize: '1rem',
    color: '#aaa',
    lineHeight: 1.8,
    marginBottom: 40,
  },
  heroBottom: {
    position: 'absolute',
    bottom: 0,
    left: 32,
    right: 32,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    paddingBottom: 20,
    zIndex: 2,
  },
  statRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 24,
    padding: '28px 32px',
    borderLeft: '2px solid rgba(62, 117, 98, 0.15)',
    transition: '0.4s',
  },
  statNum: {
    fontSize: '2.2rem',
    fontWeight: 800,
    color: '#3E7562',
    flexShrink: 0,
    lineHeight: 1,
    minWidth: 80,
  },
  statLabel: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#fff',
    marginBottom: 6,
    lineHeight: 1.4,
  },
  statDesc: {
    fontSize: '0.85rem',
    color: '#777',
    lineHeight: 1.6,
  },
  serviceCard: {
    padding: 0,
    background: '#161618',
    border: '1px solid rgba(255,255,255,0.04)',
    transition: '0.4s',
    overflow: 'hidden',
  },
  serviceTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
    padding: '0 28px',
  },
  serviceNum: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#3E7562',
    letterSpacing: 2,
  },
  serviceTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    marginBottom: 12,
    letterSpacing: '-0.5px',
    padding: '0 28px',
  },
  serviceDesc: {
    fontSize: '0.9rem',
    color: '#888',
    lineHeight: 1.7,
    marginBottom: 20,
    padding: '0 28px',
  },
  serviceTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    padding: '0 28px 28px',
  },
  serviceTag: {
    padding: '5px 14px',
    border: '1px solid rgba(62, 117, 98, 0.15)',
    color: '#777',
    fontSize: '0.72rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  aiCard: {
    padding: 32,
    border: '1px solid rgba(255,255,255,0.04)',
    background: '#161618',
    textAlign: 'center',
    transition: '0.4s',
  },
  aiLabel: {
    fontSize: '0.95rem',
    fontWeight: 600,
    marginBottom: 8,
    marginTop: 8,
    color: '#fff',
  },
  aiDesc: {
    fontSize: '0.8rem',
    color: '#777',
    lineHeight: 1.6,
  },
  ctaBlock: {
    padding: '100px 48px',
    background: '#111112',
    border: '1px solid rgba(62, 117, 98, 0.08)',
    position: 'relative',
    overflow: 'hidden',
  },
};
